/**
 * Tracks consecutive edit/write failures per file path so the session can escalate
 * from "retry the edit" to "re-read the region" instead of letting the model retry blind.
 */

import type { ImageContent, TextContent } from "@elyracode/ai";

export class EditFailureTracker {
	private readonly _counts = new Map<string, number>();

	constructor(private readonly threshold = 2) {}

	/** Record an edit outcome for a path. Returns the consecutive failure count for that path after this call. */
	record(path: string, failed: boolean): number {
		if (!failed) {
			this._counts.delete(path);
			return 0;
		}
		const next = (this._counts.get(path) ?? 0) + 1;
		this._counts.set(path, next);
		return next;
	}

	/** Consecutive failures for a path (0 if none). */
	count(path: string): number {
		return this._counts.get(path) ?? 0;
	}

	/** Whether the consecutive failure count for a path has reached the escalation threshold. */
	shouldEscalate(path: string): boolean {
		return this.count(path) >= this.threshold;
	}

	reset(path?: string): void {
		if (path === undefined) {
			this._counts.clear();
		} else {
			this._counts.delete(path);
		}
	}
}

export function buildEscalationHint(path: string, failures: number, hasReadTool: boolean): string {
	const readSentence = hasReadTool
		? " Stop retrying blind: read the file region you intend to change (use read with offset/limit around the target) and rebuild oldText from the fresh content."
		: " Stop retrying blind: rebuild oldText from the file's current content, not from memory.";
	return `\n\n[Elyra] This is failure #${failures} editing ${path} in a row.${readSentence} If the change is larger than a few lines, consider write for the whole region instead.`;
}

/** Extract the file path from edit/write tool arguments (`path` or legacy `file_path`). */
export function getEditTargetPath(args: unknown): string | undefined {
	if (!args || typeof args !== "object") return undefined;
	const record = args as { path?: unknown; file_path?: unknown };
	if (typeof record.path === "string" && record.path.length > 0) return record.path;
	if (typeof record.file_path === "string" && record.file_path.length > 0) return record.file_path;
	return undefined;
}

/** Append text to the last text block of a tool result, creating one if there is none. Returns a new array. */
export function appendTextToContent(
	content: (TextContent | ImageContent)[],
	text: string,
): (TextContent | ImageContent)[] {
	for (let i = content.length - 1; i >= 0; i--) {
		const block = content[i];
		if (block.type === "text") {
			const next = [...content];
			next[i] = { ...block, text: block.text + text };
			return next;
		}
	}
	return [...content, { type: "text", text: text.trimStart() }];
}
